import { useEffect, useMemo } from 'react'

import { careLogAreaFilterOptions } from './-care-log-area-filter'
import { MoneySection, UsageSection } from './-my-grower-report-sections'
import type { GrowerReportPeriod } from '@/lib/my-grower-report'
import { PageBackButton } from '@/components/common/PageBackButton'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAgriMaterialUsage } from '@/hooks/use-agri-material-usage-query'
import {
    useCareLogGrowerAreas,
    useMyGrowers,
} from '@/hooks/use-care-logs-query'
import { useGrowerPaymentSummary } from '@/hooks/use-grower-payment-query'
import { useMyGrowerReportTracking } from '@/hooks/use-my-grower-report-tracking'
import {
    DEFAULT_GROWER_REPORT_PERIOD,
    GROWER_REPORT_PERIODS,
    buildMoneyChart,
    buildUsageCharts,
    formatReportDate,
    growerReportToday,
    growerReportWindow,
    parseGrowerReportPeriod,
} from '@/lib/my-grower-report'
import { m } from '@/lib/paraglide/messages'

export interface MyReportsSearch {
    period?: GrowerReportPeriod
    grower?: string
    area?: string
}

/** Giá trị của mục "Tất cả bãi" — Radix Select không nhận value rỗng. */
const ALL_AREAS = 'all'

function optionalString(raw: unknown): string | undefined {
    return typeof raw === 'string' && raw ? raw : undefined
}

export function validateMyReportsSearch(
    search: Record<string, unknown>,
): MyReportsSearch {
    const period = parseGrowerReportPeriod(search.period)
    return {
        // Kỳ mặc định không ghi lên URL — địa chỉ vào màn vẫn sạch.
        period: period === DEFAULT_GROWER_REPORT_PERIOD ? undefined : period,
        grower: optionalString(search.grower),
        area: optionalString(search.area),
    }
}

const PERIOD_LABEL: Record<GrowerReportPeriod, () => string> = {
    week: () => m.my_grower_report_period_week(),
    month: () => m.my_grower_report_period_month(),
    year: () => m.my_grower_report_period_year(),
}

/**
 * Báo cáo của tài khoản hộ trồng chè (M-45): lượng phân bón, thuốc đã dùng và
 * tiền chè đã nhận theo Tuần / Tháng / Năm, lọc theo bãi (vùng trồng).
 *
 * Kỳ, hộ, bãi nằm trên URL (F5 / gửi link / back giữ nguyên). Phạm vi hộ do
 * server ép — màn chỉ chọn trong các hộ gắn với tài khoản.
 */
export function MyGrowerReportScreen({
    search,
    onSearchChange,
}: {
    search: MyReportsSearch
    /** Ghi bộ lọc mới lên URL (thay thế, không thêm lịch sử). */
    onSearchChange: (next: MyReportsSearch) => void
}) {
    const period = search.period ?? DEFAULT_GROWER_REPORT_PERIOD

    const growersQuery = useMyGrowers(true)
    const growers = growersQuery.data ?? []
    const grower = growers.find((g) => g.id === search.grower) ?? growers[0]
    const growerId = grower?.id ?? ''

    const areasQuery = useCareLogGrowerAreas(growerId || null)
    const areaOptions = careLogAreaFilterOptions(grower ? [grower] : [], [
        areasQuery.data ?? [],
    ])
    const areaId = search.area ?? ''
    const areaKnown = areaOptions.some((o) => o.value === areaId)
    // `?area=` không thuộc hộ đang xem (link cũ, bãi đã gỡ): bỏ đi — nhưng chỉ
    // khi danh sách bãi đã tải xong, kẻo xoá nhầm bãi hợp lệ.
    const staleArea =
        !!areaId && areasQuery.isSuccess && !areasQuery.isFetching && !areaKnown
    useEffect(() => {
        if (!staleArea) return
        onSearchChange({ ...search, area: undefined })
    }, [staleArea, search, onSearchChange])
    const activeArea = areaKnown ? areaId : ''

    // "Hôm nay" đọc lại mỗi lần render (rẻ): màn để mở qua nửa đêm thì lần
    // render kế tiếp (refetch khi quay lại tab) chuyển sang kỳ của ngày mới.
    const today = growerReportToday()
    const reportWindow = useMemo(
        () => growerReportWindow(period, today),
        [period, today],
    )
    const usageQuery = useAgriMaterialUsage(
        {
            from: reportWindow.from,
            to: reportWindow.to,
            grower_id: growerId,
            area_id: activeArea || undefined,
            group_by: period,
        },
        { enabled: !!growerId },
    )
    const moneyQuery = useGrowerPaymentSummary(
        {
            grower_id: growerId,
            from_date: reportWindow.from,
            to_date: reportWindow.to,
            group_by: period,
        },
        { enabled: !!growerId },
    )

    const usageRows = usageQuery.data ?? []
    const fertilizer = buildUsageCharts(
        usageRows,
        'fertilizer',
        reportWindow.starts,
    )
    const pesticide = buildUsageCharts(
        usageRows,
        'pesticide',
        reportWindow.starts,
    )
    const hasSplit = (category: string) =>
        usageRows.some((r) => r.has_split && r.material_category === category)
    const money = moneyQuery.data
    const rangeText = m.my_grower_report_window({
        from: formatReportDate(reportWindow.from),
        to: formatReportDate(reportWindow.to),
    })

    const settled =
        !!growerId &&
        !usageQuery.isFetching &&
        !moneyQuery.isFetching &&
        (usageQuery.isSuccess || usageQuery.isError) &&
        (moneyQuery.isSuccess || moneyQuery.isError)
    useMyGrowerReportTracking({
        growerId,
        period,
        areaId: activeArea,
        multiGrower: growers.length > 1,
        settled,
        outcome:
            usageQuery.isError || moneyQuery.isError
                ? 'load_error'
                : fertilizer.length + pesticide.length > 0 ||
                    (money?.count ?? 0) > 0
                  ? 'has_data'
                  : 'empty',
    })

    const setSearch = (patch: Partial<MyReportsSearch>) =>
        onSearchChange({ ...search, ...patch })

    if (growersQuery.isLoading) {
        return (
            <div className="p-4 text-sm text-muted-foreground md:p-6">
                {m.my_grower_report_loading()}
            </div>
        )
    }
    if (growersQuery.isError) {
        return (
            <div className="space-y-3 page-pad">
                <p className="rounded-2xl border bg-card p-5 text-sm text-muted-foreground">
                    {m.my_grower_report_load_error()}
                </p>
                <Button
                    type="button"
                    onClick={() => void growersQuery.refetch()}
                >
                    {m.my_grower_report_retry()}
                </Button>
            </div>
        )
    }
    if (!grower) {
        return (
            <div className="page-pad">
                <p className="rounded-2xl border bg-card p-5 text-sm text-muted-foreground">
                    {m.my_grower_empty()}
                </p>
            </div>
        )
    }

    const usageState = {
        isLoading: usageQuery.isLoading,
        isError: usageQuery.isError,
        onRetry: () => void usageQuery.refetch(),
    }

    return (
        <div className="space-y-4 page-pad md:space-y-6">
            <div className="flex min-w-0 items-start gap-2">
                <PageBackButton className="-my-2 md:-my-1" />
                <header className="space-y-1">
                    <h1 className="text-2xl font-semibold">
                        {m.my_grower_report_title()}
                    </h1>
                    <p className="text-base text-muted-foreground">
                        {m.my_grower_report_subtitle()}
                    </p>
                </header>
            </div>

            <div className="space-y-3 rounded-2xl border bg-card p-4">
                <div className="space-y-1.5">
                    <p
                        className="text-base font-medium"
                        id="my-report-period-label"
                    >
                        {m.my_grower_report_period_label()}
                    </p>
                    <Tabs
                        value={period}
                        onValueChange={(v) => {
                            const next = parseGrowerReportPeriod(v)
                            if (!next) return
                            setSearch({
                                period:
                                    next === DEFAULT_GROWER_REPORT_PERIOD
                                        ? undefined
                                        : next,
                            })
                        }}
                    >
                        <TabsList
                            aria-labelledby="my-report-period-label"
                            className="grid min-h-14 w-full grid-cols-3"
                        >
                            {GROWER_REPORT_PERIODS.map((p) => (
                                <TabsTrigger
                                    key={p}
                                    value={p}
                                    className="min-h-12 text-lg"
                                >
                                    {PERIOD_LABEL[p]()}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                    {growers.length > 1 && (
                        <div className="space-y-1.5">
                            <Label
                                htmlFor="my-report-grower"
                                className="text-base"
                            >
                                {m.my_grower_report_grower_label()}
                            </Label>
                            <Select
                                value={growerId}
                                // Bãi thuộc về hộ: đổi hộ thì bỏ bãi đang chọn.
                                onValueChange={(v) =>
                                    setSearch({ grower: v, area: undefined })
                                }
                            >
                                <SelectTrigger
                                    id="my-report-grower"
                                    className="h-12 text-lg md:h-12 md:text-lg"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {growers.map((g) => (
                                        <SelectItem
                                            key={g.id}
                                            value={g.id}
                                            className="py-2.5 text-lg"
                                        >
                                            {g.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <div className="space-y-1.5">
                        <Label htmlFor="my-report-area" className="text-base">
                            {m.my_grower_report_area_label()}
                        </Label>
                        <Select
                            value={activeArea || ALL_AREAS}
                            onValueChange={(v) =>
                                setSearch({
                                    area: v === ALL_AREAS ? undefined : v,
                                })
                            }
                        >
                            <SelectTrigger
                                id="my-report-area"
                                className="h-12 text-lg md:h-12 md:text-lg"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem
                                    value={ALL_AREAS}
                                    className="py-2.5 text-lg"
                                >
                                    {m.my_grower_report_area_all()}
                                </SelectItem>
                                {areaOptions.map((o) => (
                                    <SelectItem
                                        key={o.value}
                                        value={o.value}
                                        className="py-2.5 text-lg"
                                    >
                                        {o.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <p className="text-base text-muted-foreground">{rangeText}</p>
            </div>

            <UsageSection
                title={m.my_grower_report_fertilizer_title()}
                charts={fertilizer}
                period={period}
                starts={reportWindow.starts}
                rangeText={rangeText}
                hasSplit={hasSplit('fertilizer')}
                state={usageState}
            />
            <UsageSection
                title={m.my_grower_report_pesticide_title()}
                charts={pesticide}
                period={period}
                starts={reportWindow.starts}
                rangeText={rangeText}
                hasSplit={hasSplit('pesticide')}
                state={usageState}
            />
            <MoneySection
                rows={buildMoneyChart(money?.periods, reportWindow.starts)}
                total={money?.total_amount ?? 0}
                count={money?.count ?? 0}
                period={period}
                starts={reportWindow.starts}
                rangeText={rangeText}
                areaFiltered={!!activeArea}
                state={{
                    isLoading: moneyQuery.isLoading,
                    isError: moneyQuery.isError,
                    onRetry: () => void moneyQuery.refetch(),
                }}
            />
        </div>
    )
}
